import { BaseUITools } from "@/uiControls/base/baseUITools";
import { NumericInputUI } from "@/uiControls/base/ui/numericInputUI";

import { animationUtils } from "@/utils/animationUtils";
import { globalConfig, modelDialogTools } from "@/utils/globalConfig";
import { createTimelineControlUI, TimelineControlUI, Keyframe } from "../base/components/timelineControlUI";
import { VisFrameInfo } from "@/visionaryControllers/animationController";
import { getI18nText } from "@/utils/commonFunc";

// 模型辅助工具，扩展多视窗，与Scene无绑定关系，单独使用
export class TimelinePanelTools extends BaseUITools {
    private isExpanded: boolean = false; // 是否展开
    private isActive: boolean = false; // 是否激活
    private timelineControl: TimelineControlUI | null = null;
    private playBtnElement: HTMLElement | null = null;
    private pauseBtnElement: HTMLElement | null = null;
    private prevFrameBtnElement: HTMLElement | null = null;
    private nextFrameBtnElement: HTMLElement | null = null;
    private addFrameBtnElement: HTMLElement | null = null;
    private removeFrameBtnElement: HTMLElement | null = null;
    private totalFramesElement: NumericInputUI | null = null;

    constructor(id: string) {
        super(id, false);
        this.init();
    }

    init() {
        const panelContainerElement = this.ui.createElement({
            tag: 'div',
            className: 'panel-timeline-container',
            id: 'panel-timeline-container',
        });
        const titleElement = this.initTitleElements();
        panelContainerElement.appendChild(titleElement);

        const contentElement = this.initContentElements();
        panelContainerElement.appendChild(contentElement);

        this.appendChild('panelContainer', panelContainerElement);

        setTimeout(() => {
            this.setSelectionDropdown('frameRateControl');
        }, 500);
        this.setupEventListeners();
    }

    initTitleElements() {
        const titleElement = this.ui.createElement({
            tag: 'div',
            className: 'panel-timeline-title',
        });
        const titleLeftElement = this.ui.createElement({
            tag: 'div',
            className: 'panel-timeline-title-left',
        });
        const leftLabelElement = this.ui.createElement({
            tag: 'div',
            className: 'panel-timeline-title-left-label',
            textContent: '时间轴',
            i18nKey: 'label.timeline',
        });
        const leftExpandBtnElement = this.ui.createButton({
            iconName: 'ExpandOutlined',
            isIconOnly: true,
            className: ['btn-item', 'btn-icon-item', 'inherit-font-size', 'btn-expand-button'],
        });
        leftExpandBtnElement.addEventListener('click', () => {
            this.setExpanded(!this.isExpanded);
        });
        titleLeftElement.appendChild(leftLabelElement);
        titleLeftElement.appendChild(leftExpandBtnElement);

        /////////////// 中间，多个动画按钮////////////////////////////////////////////////////////////////
        const titleMiddleElement = this.initTitleControlElements();

        ////////////////// 右边帧率、总帧数//////////////////////////////////////////////////////////////
        const titleRightElement = this.ui.createElement({
            tag: 'div',
            className: 'panel-timeline-title-right',
        });
        const frameRateControl = document.createElement('div');
        frameRateControl.className = 'numeric-inputui-container flex-direction-column';
        frameRateControl.id = 'frame-rate-control';
        frameRateControl.innerHTML = `
        <div class="dropdown w100 pannel-timeline-dropdown">
        <button class="btn btn-secondary dropdown-toggle vision-dropdown-toggle w100"
                                            type="button" id="frameRateControlButton" data-bs-toggle="dropdown"
                                            aria-expanded="false">30 FPS
                                        </button>
                                        <ul class="dropdown-menu vision-dropdown-menu w100" id="frameRateControlListDom"
                                            aria-labelledby="frameRateControlButton">
                                            <li><a class="dropdown-item vision-dropdown-item" href="#"
                                                    data-value="24">24 FPS</a></li>
                                            <li><a class="dropdown-item vision-dropdown-item active" href="#"
                                                    data-value="30">30 FPS</a></li>
                                            <li><a class="dropdown-item vision-dropdown-item" href="#"
                                                    data-value="60">60 FPS</a></li>
                                        </ul>
                                        </div>
                
        `;

        titleRightElement.appendChild(frameRateControl);
        const totalFramesControl = new NumericInputUI({
            id: 'total-frames-control',
            label: '总帧数',
            min: 1,
            max: 10000,
            step: 10,
            precision: 0,
            value: globalConfig.totalFrames,
            showValue: false,
            showLabel: false,
            valueFormat: (value) => `${value} 帧`,
            i18nKey: 'label.totalFrames',
        });
        totalFramesControl.onChange((value) => { // 总帧数改变，影响时间轴渲染
            console.log('[timeline-panel-tools] totalFramesControl onChange', value);
            if (value !== null && !isNaN(value)) {
                animationUtils.setTotalFrames(value);
                this.timelineControl?.setTotalTicks(value);
            }
        });
        this.totalFramesElement = totalFramesControl;
        titleRightElement.appendChild(totalFramesControl.getContainer());

        titleElement.appendChild(titleLeftElement);
        titleElement.appendChild(titleMiddleElement);
        titleElement.appendChild(titleRightElement);

        return titleElement;
    }

    initTitleControlElements() {
        const titleControlElements = this.ui.createElement({
            tag: 'div',
            className: 'panel-timeline-title-middle',
        });

        const prevFrameBtnElement = this.ui.createButton({
            iconName: 'LastFilled',
            isIconOnly: true,
            className: ['btn-item', 'btn-icon-item', 'inherit-font-size'],
        });
        prevFrameBtnElement.addEventListener('click', () => {
            this.onPrevFrame();
        });
        this.prevFrameBtnElement = prevFrameBtnElement;
        this.prevFrameBtnElement.setAttribute('disabled', 'disabled');
        titleControlElements.appendChild(prevFrameBtnElement);

        const playBtnElement = this.ui.createButton({
            // iconName: 'PlayOutlined',
            iconName: 'PlayFilled',
            isIconOnly: true,
            className: ['btn-item', 'btn-icon-item', 'inherit-font-size', 'btn-icon-play-item'],
        });

        playBtnElement.addEventListener('click', () => {
            this.handleAnimationPlay(true);
        });
        this.playBtnElement = playBtnElement;
        titleControlElements.appendChild(playBtnElement);

        const pauseBtnElement = this.ui.createButton({
            iconName: 'PauseFilled',
            isIconOnly: true,
            className: ['btn-item', 'btn-icon-item', 'inherit-font-size', 'hidden'],
        });
        pauseBtnElement.addEventListener('click', () => {
            this.handleAnimationPlay(false);
        });
        this.pauseBtnElement = pauseBtnElement;
        titleControlElements.appendChild(pauseBtnElement);

        const nextFrameBtnElement = this.ui.createButton({
            iconName: 'NextFilled',
            isIconOnly: true,
            className: ['btn-item', 'btn-icon-item', 'inherit-font-size'],
        });
        nextFrameBtnElement.addEventListener('click', () => {
            this.onNextFrame();
        });
        this.nextFrameBtnElement = nextFrameBtnElement;
        this.nextFrameBtnElement.setAttribute('disabled', 'disabled');
        titleControlElements.appendChild(nextFrameBtnElement);

        const addFrameBtnElement = this.ui.createButton({
            iconName: 'AddOutlined',
            isIconOnly: true,
            className: ['btn-item', 'btn-icon-item', 'inherit-font-size'],
        });
        addFrameBtnElement.addEventListener('click', () => {
            this.addFrame();
        });
        this.addFrameBtnElement = addFrameBtnElement;
        titleControlElements.appendChild(addFrameBtnElement);

        const removeFrameBtnElement = this.ui.createButton({
            iconName: 'MinusOutlined',
            isIconOnly: true,
            className: ['btn-item', 'btn-icon-item', 'inherit-font-size'],
        });
        removeFrameBtnElement.addEventListener('click', () => {
            // 二次确认是否确定移除关键帧
            const currentTick = this.timelineControl?.getCurrentTick() || 0;
            const removeFrameTitle = getI18nText('message.removeFrame.title');
            const removeFrameConfirmBefore = getI18nText('message.removeFrame.confirmBefore');
            const removeFrameConfirmAfter = getI18nText('message.removeFrame.confirmAfter');
            modelDialogTools.showConfirmDialog(
                removeFrameTitle,
                `${removeFrameConfirmBefore} "${currentTick}" ${removeFrameConfirmAfter}`,
                () => {
                    this.removeFrame();
                },
                () => {
                    // console.log('用户取消删除关键帧');
                }
            );
        });
        this.removeFrameBtnElement = removeFrameBtnElement;
        this.removeFrameBtnElement.setAttribute('disabled', 'disabled');
        titleControlElements.appendChild(removeFrameBtnElement);

        return titleControlElements;
    }

    initContentElements() {
        const contentElement = this.ui.createElement({
            tag: 'div',
            className: 'panel-timeline-content',
        });

        // 创建时间轴控制组件
        this.timelineControl = createTimelineControlUI({
            id: 'timeline-control',
            totalTicks: globalConfig.totalFrames || 120,
            labelCount: 30
        });

        // // 监听刻度点击事件 - 场景跳转到刻度位置
        this.timelineControl.onTickClick((tick: number) => {
            console.log('Tick clicked:', tick);
            animationUtils.handleUpdateFrameIndex(tick);

            // 查找当前tick前面和后面的keyframe
            if (this.timelineControl) {
                const keyframes = this.timelineControl.getKeyframes();

                // 查找前面的keyframe（position < tick，取其中position最大的）
                const prevKeyframes = keyframes.filter(k => k.position < tick);
                if (prevKeyframes.length > 0) {
                    const prevKeyframe = prevKeyframes.reduce((prev, current) => {
                        return current.position > prev.position ? current : prev;
                    });
                    console.log('前面的keyframe:', {
                        id: prevKeyframe.id,
                        position: prevKeyframe.position,
                        index: prevKeyframe.index,
                        data: prevKeyframe.data
                    });

                } else {
                    console.log('未找到前面的keyframe');
                }

                // 查找后面的keyframe（position > tick，取其中position最小的）
                const nextKeyframes = keyframes.filter(k => k.position > tick);
                if (nextKeyframes.length > 0) {
                    const nextKeyframe = nextKeyframes.reduce((prev, current) => {
                        return current.position < prev.position ? current : prev;
                    });
                    console.log('后面的keyframe:', {
                        id: nextKeyframe.id,
                        position: nextKeyframe.position,
                        index: nextKeyframe.index,
                        data: nextKeyframe.data
                    });
                } else {
                    console.log('未找到后面的keyframe');
                }

                // 更新按钮状态
                this.updateFrameButtonsState();
            }
        });

        contentElement.appendChild(this.timelineControl.getContainer());

        return contentElement;
    }

    setSelectionDropdown(domid: string) {
        const dropdownItems = document.querySelectorAll(`#${domid}ListDom .vision-dropdown-item`);
        const dropdownToggle = document.getElementById(`${domid}Button`);

        dropdownItems.forEach(item => {
            item.addEventListener('click', (event) => {
                event.preventDefault();
                const selectedValue = item.getAttribute('data-value');
                const selectedText = item.textContent;

                // 设置选中项
                const frameRate = typeof selectedValue === 'string' ? parseInt(selectedValue) : Array.isArray(selectedValue) ? parseInt(selectedValue[0]) : 30;
                // 控制台打印选中的选项
                console.log('切换帧率:', selectedValue, frameRate);

                if (!isNaN(frameRate)) {
                    animationUtils.setFrameRate(frameRate);
                    if (this.timelineControl) {
                        this.timelineControl.setFrameRate(frameRate);
                    }
                }
                // 更新按钮文本
                if (dropdownToggle) {
                    dropdownToggle.textContent = selectedText || selectedValue;
                }

                // 更新active状态
                dropdownItems.forEach(item => {
                    const itemValue = item.getAttribute('data-value');
                    if (itemValue === selectedValue) {
                        item.classList.add('active');
                    } else {
                        item.classList.remove('active');
                    }
                });
            });
        });
    }

    setupEventListeners() {
        window.addEventListener('keyup', (event: KeyboardEvent) => {
            if (event.key === 'i' || event.key === 'I') {
                if (this.isExpanded) {
                    this.addFrame();
                }
            }
        });
    }

    setExpanded(expanded: boolean) {
        if(this.isExpanded === expanded) return;
        this.isExpanded = expanded;
        if (this.isExpanded) {
            document.getElementById('mainFooterContainer')?.classList.add('timeline-container-expanded');
        } else {
            document.getElementById('mainFooterContainer')?.classList.remove('timeline-container-expanded');
        }
        window.dispatchEvent(new Event('resize'));
    }

    updateTimelineWithKeyframes(keyframes: VisFrameInfo[], isReset: boolean = false) {
        console.log('更新时间轴关键帧：', keyframes,isReset);
        if (this.timelineControl) {
            if(keyframes.length > 0){
                console.log('最后一个关键帧---总帧数',keyframes[keyframes.length - 1].index,this.totalFramesElement?.getValue());
                if(this.totalFramesElement?.getValue() && this.totalFramesElement?.getValue() < keyframes[keyframes.length - 1].index + 1){
                    this.totalFramesElement?.syncFromInput(keyframes[keyframes.length - 1].index + 1);
                }
            }
            if (isReset) {
                this.timelineControl.clearKeyframes();
                this.timelineControl.setKeyframes(keyframes);
                this.timelineControl.setCurrentTick(0);
            } else {
                this.timelineControl.setKeyframes(keyframes);
            }
        }
    }

    setTotalFrames(totalFrames: number) {
        if (this.totalFramesElement) {
            this.totalFramesElement.syncFromInput(totalFrames);
        }
    }

    handleAnimationPlay(play: boolean) { // 设置动画开启/停止
        if (this.timelineControl) {
            if (play) {
                this.playBtnElement?.classList.add('hidden');
                this.pauseBtnElement?.classList.remove('hidden');
                this.timelineControl.setAnimationPlay(true);
            } else {
                this.playBtnElement?.classList.remove('hidden');
                this.pauseBtnElement?.classList.add('hidden');
                this.timelineControl.setAnimationPlay(false);
            }
        }
    }

    /**
     * 更新前一帧、后一帧和移除帧按钮的启用/禁用状态
     */
    private updateFrameButtonsState() {
        if (!this.timelineControl) return;

        const currentTick = this.timelineControl.getCurrentTick();
        const keyframes = this.timelineControl.getKeyframes();

        // 检查是否有前一帧
        const prevKeyframes = keyframes.filter(k => k.position < currentTick);
        const hasPrevFrame = prevKeyframes.length > 0;

        // 检查是否有后一帧
        const nextKeyframes = keyframes.filter(k => k.position > currentTick);
        const hasNextFrame = nextKeyframes.length > 0;

        // 检查当前tick是否有关键帧
        const keyframeAtCurrentTick = keyframes.find(k => k.position === currentTick);
        const hasKeyframeAtCurrentTick = !!keyframeAtCurrentTick;

        // 更新前一帧按钮状态
        if (this.prevFrameBtnElement) {
            if (hasPrevFrame) {
                this.prevFrameBtnElement.removeAttribute('disabled');
            } else {
                this.prevFrameBtnElement.setAttribute('disabled', 'disabled');
            }
        }

        // 更新后一帧按钮状态
        if (this.nextFrameBtnElement) {
            if (hasNextFrame) {
                this.nextFrameBtnElement.removeAttribute('disabled');
            } else {
                this.nextFrameBtnElement.setAttribute('disabled', 'disabled');
            }
        }

        // 更新移除帧按钮状态（只有在当前tick有关键帧时才启用）
        if (this.removeFrameBtnElement) {
            if (hasKeyframeAtCurrentTick) {
                this.removeFrameBtnElement.removeAttribute('disabled');
            } else {
                this.removeFrameBtnElement.setAttribute('disabled', 'disabled');
            }
        }
    }
    onPrevFrame() {
        if (this.timelineControl) {
            const currentTick = this.timelineControl.getCurrentTick();
            const keyframes = this.timelineControl.getKeyframes();

            // 查找当前tick的前一帧：找出所有position < currentTick的关键帧，然后取其中position最大的
            const prevKeyframes = keyframes.filter(k => k.position < currentTick);

            if (prevKeyframes.length > 0) {
                // 找到position最大的关键帧（最接近currentTick的前一帧）
                const prevKeyframe = prevKeyframes.reduce((prev, current) => {
                    return current.position > prev.position ? current : prev;
                });

                // 跳转到前一帧
                this.timelineControl.setCurrentTick(prevKeyframe.position);
                animationUtils.handleUpdateFrameIndex(prevKeyframe.position);
            } else {
                console.log('未找到前一帧（当前tick之前没有关键帧）');
            }

            // 更新按钮状态
            this.updateFrameButtonsState();
        }
    }
    onNextFrame() {
        if (this.timelineControl) {
            const currentTick = this.timelineControl.getCurrentTick();
            const keyframes = this.timelineControl.getKeyframes();

            // 查找当前tick的后一帧：找出所有position > currentTick的关键帧，然后取其中position最小的
            const nextKeyframes = keyframes.filter(k => k.position > currentTick);

            if (nextKeyframes.length > 0) {
                // 找到position最小的关键帧（最接近currentTick的后一帧）
                const nextKeyframe = nextKeyframes.reduce((prev, current) => {
                    return current.position < prev.position ? current : prev;
                });

                // 跳转到后一帧
                this.timelineControl.setCurrentTick(nextKeyframe.position);
                animationUtils.handleUpdateFrameIndex(nextKeyframe.position);
            } else {
                console.log('未找到后一帧（当前tick之后没有关键帧）');
            }

            // 更新按钮状态
            this.updateFrameButtonsState();
        }
    }
    addFrame() {
        console.log("timeline panel tools add frame");
        if (this.timelineControl) {
            const currentTick = this.timelineControl.getCurrentTick();
            if (currentTick >= 0) {
                const keyframes = this.timelineControl.getKeyframes();
                console.log('时间轴工具添加关键帧', currentTick, keyframes);

                // // 查找当前帧是否已经存在关键帧
                // const existingKeyframe = keyframes.find(k => k.position === currentTick);

                // if (existingKeyframe) {
                //     // 当前帧已存在关键帧，先删除再添加（替换）
                //     this.timelineControl.removeKeyframe(existingKeyframe.id);
                //     console.log("Removed existing keyframe at tick:", currentTick);
                // }

                // 添加或替换关键帧
                const keyframe = this.timelineControl.addKeyframe(currentTick, { created: new Date() });
                // console.log(existingKeyframe ? "Replaced keyframe:" : "Added keyframe:", currentTick, keyframe);
                animationUtils.handleAddKeyframe(currentTick);

                // 更新按钮状态
                this.updateFrameButtonsState();
            } else {
                alert(getI18nText('errorinfo.needToLocateKeyframePosition'));
            }
        }
    }
    // 移除关键帧，同时要移除场景中的关键帧
    removeFrame() {
        if (this.timelineControl) {
            const currentTick = this.timelineControl.getCurrentTick();
            const keyframes = this.timelineControl.getKeyframes();
            // 查找当前帧是否有关键帧
            const keyframeAtCurrentTick = keyframes.find(k => k.position === currentTick);

            if (keyframeAtCurrentTick) {
                // 当前帧是关键帧，执行移除
                const removed = this.timelineControl.removeKeyframe(keyframeAtCurrentTick.id);
                console.log("移除关键帧:", removed, keyframeAtCurrentTick);
                animationUtils.handleDeleteKeyframe(keyframeAtCurrentTick.position);

                // 更新按钮状态
                this.updateFrameButtonsState();
            } else {
                // 当前帧不是关键帧，提示用户
                alert(getI18nText('errorinfo.frameNotKeyframe', { frame: currentTick + 1 }));
            }
        }
    }
    setActive(active: boolean) {
        // this.isActive = active;
        this.isActive = !this.isActive;
    }
}

